import { useEffect, useMemo, useRef } from 'react';

import { graphql } from '@/__generated__';
import { SearchQueryFilter } from '@/__generated__/graphql';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';
import { truthy } from '@/util/array';
import { zResourceTypeExplorer } from '@/util/resourceType';

export const GetAppliedSearchResults = graphql(/* GraphQL */ `
  query AppliedSearch(
    $environmentId: BigInt!
    $filter: SearchQueryFilter!
    $first: Int!
    $includeDebug: Boolean!
    $after: String
    $userId: BigInt
  ) {
    environment(id: $environmentId, userId: $userId) {
      applied {
        lastUpdatedAt
        searchResults(filter: $filter, first: $first, after: $after) {
          debug @include(if: $includeDebug)
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              matchedField
              highlight
              hit {
                name
                uniqueId
                resourceType
                ... on ModelAppliedStateNode {
                  modelHealthIssues: healthIssues
                  fqn
                }
                ... on SourceAppliedStateNode {
                  sourceHealthIssues: healthIssues
                  freshness {
                    freshnessChecked
                  }
                  fqn
                }
                ... on TestAppliedStateNode {
                  fqn
                }
                ... on SnapshotAppliedStateNode {
                  fqn
                }
                ... on SeedAppliedStateNode {
                  fqn
                }
                ... on SemanticModelDefinitionNode {
                  fqn
                }
                ... on ExposureAppliedStateNode {
                  exposureHealthIssues: healthIssues
                  fqn
                }
                ... on MetricDefinitionNode {
                  fqn
                }
              }
            }
          }
        }
      }
    }
  }
`);

type AppliedSearchParams = {
  environmentId: number | undefined;
  userId: number | 'loading' | null;
  filter: SearchQueryFilter;
  pageSize?: number;
  after?: string;
  includeDebug?: boolean;
  pause?: boolean;
};

export const useSearchResults = ({
  environmentId = 0,
  userId = -1,
  filter,
  pageSize = 50,
  includeDebug = false,
  after,
  pause,
}: AppliedSearchParams) => {
  const query = usePaginatedQuery(
    {
      query: GetAppliedSearchResults,
      variables: {
        environmentId,
        userId: userId as number | null,
        filter: {
          ...filter,
          resourceTypes: filter.resourceTypes?.length
            ? filter.resourceTypes
            : undefined,
        },
        includeDebug,
        first: pageSize,
        after,
      },
      pause: pause || !environmentId || !filter.fields.length,
    },
    (data) => {
      const { totalCount, pageInfo } = data.environment.applied?.searchResults ?? {
        totalCount: null,
        pageInfo: null,
      };
      return {
        totalCount: totalCount ?? null,
        endCursor: pageInfo?.endCursor ?? null,
        hasNextPage: pageInfo?.hasNextPage ?? false,
      };
    },
    (data) =>
      data.environment.applied?.searchResults.edges
        .map((edge) => edge.node)
        .filter(truthy),
    (data) => data.environment.applied?.lastUpdatedAt,
  );

  useCacheMonitor({ applied: query.lastUpdatedAt });

  const isLoadingMore = query.fetching;

  // Track the filter that produced the current data so we can detect when
  // a new search has been submitted but results haven't arrived yet.
  const filterKey = JSON.stringify(filter);
  const loadedFilterKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!query.fetching && query.data != null) {
      loadedFilterKeyRef.current = filterKey;
    }
  }, [query.fetching, query.data, filterKey]);

  const isNewSearch = loadedFilterKeyRef.current !== filterKey;
  const isInitialLoad = query.fetching && (query.data == null || isNewSearch);

  return useMemo(() => {
    return {
      ...query,
      data:
        query.data && !isNewSearch
          ? query.data.map((d) => ({
              ...d,
              hit: {
                ...d.hit,
                // TODO: can we do this in codex-api?
                resourceType: zResourceTypeExplorer.parse(d.hit.resourceType),
              },
            }))
          : undefined,
      isLoadingMore,
      isInitialLoad,
    };
  }, [query, isLoadingMore, isInitialLoad, isNewSearch]);
};
